import { FETCH_QUERIES_NAME } from '@common/constants';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { DisplayWrapper } from '@core/DisplayWrapper/DisplayWrapper';
import {
  ComponentType,
  Form,
  FormFileUploader,
  FormTextField,
  Grid,
  IItemResponse,
  LoadingButton,
  requiredRule,
  useForm,
} from '@gcl/react-core';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useCreateMySignatureAsync, useGetMySignatureAsync, useUpdateMySignatureAsync } from './userProfile.queries';
import {
  ICreateMySignatureRequest,
  ISignature,
  IUpdateMySignatureRequest,
  mySignatureFormKeys,
} from './userProfile.models';
import './MySignatureDetail.scss';

export interface IMySignatureDetailProps {}

export const MySignatureDetail: React.FC<IMySignatureDetailProps> = ({}) => {
  const useFormMethods = useForm({
    mode: 'onBlur',
    shouldFocusError: true,
  });
  const { formState, reset, setError } = useFormMethods;

  const {
    error: errorGetMySignature,
    isLoading: isLoadingMySignature,
    data: dataMySignature,
  } = useGetMySignatureAsync(!formState.isDirty);

  const {
    mutate: mutateCreateMySignature,
    isLoading: isLoadingCreateMySignature,
    data: dataCreateMySignature,
    isSuccess: isSuccessCreateMySignature,
    isError: isErrorCreateMySignature,
    error: errorCreateMySignature,
  } = useCreateMySignatureAsync();

  const {
    mutate: mutateUpdateMySignature,
    isLoading: isLoadingUpdateMySignature,
    data: dataUpdateMySignature,
    isSuccess: isSuccessUpdateMySignature,
    isError: isErrorUpdateMySignature,
    error: errorUpdateMySignature,
  } = useUpdateMySignatureAsync();

  const isUpdate = React.useMemo(() => {
    return !!dataMySignature?.item?.id;
  }, [JSON.stringify(dataMySignature)]);

  const convertServerStateToFormState = (serverSignature: IItemResponse<ISignature>) => {
    // Do not map server signature image to the form uploader
    const value = { ...serverSignature.item, fileContent: null };
    return value;
  };

  React.useEffect(() => {
    const convertFile = async () => {
      if (dataMySignature?.isSuccess && dataMySignature?.item) {
        reset(convertServerStateToFormState(dataMySignature));
      }
    };
    convertFile();
  }, [JSON.stringify(dataMySignature)]);

  const queryClient = useQueryClient();

  React.useEffect(() => {
    if (isSuccessCreateMySignature) {
      reset(convertServerStateToFormState(dataCreateMySignature));
      queryClient.invalidateQueries([FETCH_QUERIES_NAME.GET_MY_SIGNATURE]);
    }
    if (isSuccessUpdateMySignature) {
      reset(convertServerStateToFormState(dataUpdateMySignature));
      queryClient.invalidateQueries([FETCH_QUERIES_NAME.GET_MY_SIGNATURE]);
    }
  }, [isSuccessCreateMySignature, isSuccessUpdateMySignature]);

  const onSubmit = (data: { [x: string]: any }) => {
    if (isUpdate) {
      const request: IUpdateMySignatureRequest = {
        id: dataMySignature?.item?.id as number,
        fileContent: data.fileContent?.files?.[0],
        fileName: data.fileContent?.files?.[0]?.name,
        qualification: data.qualification,
        rowVersion: dataMySignature?.item?.rowVersion as string,
      };
      mutateUpdateMySignature(request);
      return;
    }
    const request: ICreateMySignatureRequest = {
      fileContent: data.fileContent.files[0],
      fileName: data.fileContent.files[0].name,
      qualification: data.qualification,
    };
    mutateCreateMySignature(request);
  };

  return (
    <>
      <ApiErrorDisplay
        setFormError={setError}
        errors={[
          { error: errorCreateMySignature, genericError: 'Create signature failed' },
          { error: errorUpdateMySignature, genericError: 'Update signature failed' },
          { error: errorGetMySignature, genericError: 'Get signature failed' },
        ]}
      />
      <DisplayWrapper
        isLoading={isLoadingMySignature}
        loaderProps={[
          { suffixBlankRow: true, numberOfRows: 1, lineHeight: 20, lineWidth: 20 },
          { suffixBlankRow: true, numberOfRows: 1, lineHeight: 20, lineWidth: 350 },
          { suffixBlankRow: true, numberOfRows: 1, lineHeight: 20, lineWidth: 300 },
          { suffixBlankRow: true, numberOfRows: 1, lineHeight: 20, lineWidth: 320 },
        ]}
      >
        <Form
          useFormMethods={useFormMethods}
          onSubmit={onSubmit}
          render={(formFieldMethods) => {
            return (
              <>
                <Grid
                  className="gcl-mb-xl gcl-gems-signature-top-bar"
                  container
                  flexWrap="nowrap"
                  justifyContent="space-between"
                >
                  <LoadingButton
                    type={ComponentType.Primary}
                    onClick={() => {
                      useFormMethods.clearErrors();
                      useFormMethods.handleSubmit(onSubmit)();
                    }}
                    isLoading={isLoadingCreateMySignature || isLoadingUpdateMySignature}
                    isError={isErrorCreateMySignature || isErrorUpdateMySignature}
                    isSuccess={isSuccessCreateMySignature || isSuccessUpdateMySignature}
                    timeToResetSuccessState={3000}
                    timeToResetErrorState={5000}
                  >
                    Save
                  </LoadingButton>
                </Grid>
                <Grid xs={6}>
                  <LabelWithMessage required={!isUpdate} labelFor={mySignatureFormKeys.FILE_CONTENT} text="Signature" />
                  <FormFileUploader
                    acceptFileTypes={['image/jpeg', 'image/png', 'image/bmp']}
                    id={mySignatureFormKeys.FILE_CONTENT}
                    formFieldMethods={formFieldMethods}
                    rules={!isUpdate ? requiredRule : {}}
                  />
                </Grid>
                {dataMySignature?.item?.fileContent && (
                  <>
                    <br />
                    <Grid xs={6}>
                      <LabelWithMessage labelFor={mySignatureFormKeys.EXISTING_SIGNATURE} text="Existing Signature" />

                      <img
                        id={mySignatureFormKeys.EXISTING_SIGNATURE}
                        alt={mySignatureFormKeys.EXISTING_SIGNATURE}
                        src={`data:${dataMySignature?.item?.mime};base64,${dataMySignature?.item?.fileContent}`}
                      />
                    </Grid>
                  </>
                )}
                <br />
                <Grid xs={6}>
                  <LabelWithMessage labelFor={mySignatureFormKeys.QUALIFICATION} text="Qualification" />
                  <FormTextField id={mySignatureFormKeys.QUALIFICATION} formFieldMethods={formFieldMethods} />
                </Grid>
              </>
            );
          }}
        />
      </DisplayWrapper>
    </>
  );
};
